"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = BenefitMemberCard;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const cancelable_promise_1 = require("cancelable-promise");
const ThemeContext_1 = require("../ThemeContext");
const UserContext_1 = require("../contexts/UserContext");
const Benefits_1 = __importDefault(require("../api/Benefits"));
const MyProfile_1 = __importDefault(require("../api/MyProfile"));
const Loading_1 = __importDefault(require("../components/Loading"));
const Aetna_svg_1 = __importDefault(require("../images/benefits/Aetna.svg"));
const MetLife_svg_1 = __importDefault(require("../images/benefits/MetLife.svg"));
const ResponsiveContext_1 = require("../contexts/ResponsiveContext");
const FadeInView_1 = __importDefault(require("../components/FadeInView"));
const useStyles = () => {
    const THEME = (0, ThemeContext_1.useTheme)();
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    return react_native_1.StyleSheet.create({
        cardOuter: {
            borderWidth: 1,
            borderBottomWidth: 2,
            borderColor: THEME.Gray,
            borderRadius: 6
        },
        cardHeader: {
            paddingHorizontal: size != 'sm' ? 24 : 12,
            paddingVertical: size != 'sm' ? 12 : 4,
            flexDirection: 'row',
            alignItems: 'center',
            borderTopLeftRadius: 6,
            borderTopRightRadius: 6,
            backgroundColor: '#C9CED8',
            overflow: 'hidden'
        },
        cardBody: {
            alignSelf: 'center',
            borderRadius: 6,
            backgroundColor: '#bcc8d188',
            maxWidth: 500,
            padding: (size != 'sm' ? 24 : 12),
            paddingVertical: (size != 'sm' ? 16 : 8),
            width: '100%',
            borderWidth: 1,
            borderColor: '#00000014',
            borderTopWidth: 0,
            borderBottomWidth: 2,
            borderBottomColor: '#0000001F',
            overflow: 'hidden',
        },
        cardRow: {
            flexDirection: 'row',
            alignItems: 'center',
            marginVertical: 4
        },
        textMedium: {
            color: THEME.Text,
            fontSize: size != 'sm' ? 16 : 14
        },
        textLarge: {
            color: THEME.Text,
            fontSize: size != 'sm' ? 18 : 16
        },
        textBold: {
            fontWeight: '500'
        },
    });
};
function BenefitMemberCard(props) {
    const THEME = (0, ThemeContext_1.useTheme)();
    const styles = useStyles();
    const userContext = react_1.default.useContext(UserContext_1.UserContext);
    const { size } = (0, ResponsiveContext_1.useResponsiveContext)();
    const promiseCards = react_1.default.useRef(null);
    const promiseInfo = react_1.default.useRef(null);
    const [status, setStatus] = react_1.default.useState('loading');
    const [memberCards, setMemberCards] = react_1.default.useState(null);
    const [personalInfo, setPersonalInfo] = react_1.default.useState(null);
    const [dependents] = react_1.default.useState(() => {
        if (!props.data?.dependent)
            return null;
        let array = [...props.data?.dependent];
        array.sort((a, b) => a.relation - b.relation);
        return array;
    });
    const validate = (data) => {
        switch (props.carrier) {
            case 'AETNA':
                return data.aetnaGroupId && data.aetnaMemberId;
            case 'METLIFE':
                return data.metlifeGroupId && data.metlifeMemberId;
            default:
                return false;
        }
    };
    const refresh = react_1.default.useCallback(() => {
        promiseCards.current?.cancel();
        promiseInfo.current?.cancel();
        setStatus('loading');
        promiseCards.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => {
            if (!!props.adminView)
                return Benefits_1.default.getEmployeeCards(token, props.employeeId);
            else
                return Benefits_1.default.getCards(token);
        }));
        promiseInfo.current = (0, cancelable_promise_1.cancelable)(userContext.getToken().then(token => {
            if (!!props.adminView)
                return MyProfile_1.default.getPersonalInfo(token, props.employeeId);
            else
                return MyProfile_1.default.getPersonalInfo(token);
        }));
        Promise.all([
            promiseCards.current,
            promiseInfo.current
        ]).then(([dataCards, dataInfo]) => {
            setMemberCards(dataCards);
            setPersonalInfo(dataInfo);
            if (validate(dataCards)) {
                setStatus('done');
            }
            else {
                setStatus('empty');
            }
        }).catch((e) => {
            console.error(e);
            setMemberCards(null);
            setPersonalInfo(null);
            setStatus('error');
        });
    }, []);
    react_1.default.useEffect(() => {
        refresh();
        return () => {
            promiseCards.current?.cancel();
            promiseInfo.current?.cancel();
        };
    }, []);
    const getCarrierImage = () => {
        switch (props.carrier) {
            case 'AETNA': return ((0, jsx_runtime_1.jsx)(Aetna_svg_1.default, { height: 24, width: 120 }));
            case 'METLIFE': return ((0, jsx_runtime_1.jsx)(MetLife_svg_1.default, { height: 24, width: 108 }));
            default: null;
        }
    };
    return ((0, jsx_runtime_1.jsxs)(react_native_1.View, { children: [(status == 'loading' || status == 'error')
                &&
                    (0, jsx_runtime_1.jsx)(react_native_1.View, { style: [styles.cardOuter], children: (0, jsx_runtime_1.jsx)(Loading_1.default, { retryable: true, loading: status == 'loading', error: status == 'error', onRetry: () => refresh() }) }), (status == 'empty')
                &&
                    (0, jsx_runtime_1.jsxs)(FadeInView_1.default, { style: [styles.cardOuter], children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardHeader], children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { pointerEvents: "none", style: {
                                            position: 'absolute',
                                            top: -20,
                                            left: -60,
                                            width: '160%',
                                            height: 60,
                                            backgroundColor: `${THEME.AlwaysWhite}26`,
                                            transform: [{ rotate: '-12deg' }],
                                        } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { pointerEvents: "none", style: {
                                            position: 'absolute',
                                            bottom: -30,
                                            right: -80,
                                            width: '180%',
                                            height: 80,
                                            backgroundColor: `${THEME.AlwaysWhite}14`,
                                            transform: [{ rotate: '8deg' }],
                                        } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { children: getCarrierImage() })] }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: [styles.cardBody, { borderTopLeftRadius: 0, borderTopRightRadius: 0 }], children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { marginVertical: 16 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginVertical: 4 }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontSize: size != 'sm' ? 22 : 20, textAlign: 'center' }, children: "Card is not available yet." }) }), (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { marginVertical: 4 }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: THEME.Text, fontSize: size != 'sm' ? 18 : 16, textAlign: 'center' }, children: "Please try again later." }) })] }) })] }), (status == 'done')
                &&
                    (0, jsx_runtime_1.jsxs)(FadeInView_1.default, { style: [styles.cardOuter], children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardHeader], children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { pointerEvents: "none", style: {
                                            position: 'absolute',
                                            top: -20,
                                            left: -60,
                                            width: '160%',
                                            height: 60,
                                            backgroundColor: `${THEME.AlwaysWhite}26`,
                                            transform: [{ rotate: '-12deg' }],
                                        } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { pointerEvents: "none", style: {
                                            position: 'absolute',
                                            bottom: -30,
                                            right: -80,
                                            width: '180%',
                                            height: 80,
                                            backgroundColor: `${THEME.AlwaysWhite}14`,
                                            transform: [{ rotate: '8deg' }],
                                        } }), (0, jsx_runtime_1.jsx)(react_native_1.View, { children: getCarrierImage() }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flex: 1 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: '#465166', fontSize: 11, textAlign: 'right' }, children: "Customer Service" }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: { color: '#2B3240', fontSize: 13, textAlign: 'right' }, children: props.carrier == 'AETNA' ? '1-800-704-7287' : (props.data.category == 'Vision' ? '1-800-638-5433' : '1-800-942-0854') })] })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardBody, { borderTopLeftRadius: 0, borderTopRightRadius: 0 }], children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardRow, { alignItems: 'flex-start', marginVertical: 6 }], children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, styles.textBold], children: "Name" }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { flex: 1, marginLeft: 12 }, children: [(0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flexDirection: 'row' }, children: (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, styles.textBold], children: personalInfo?.firstName + ' ' + personalInfo?.lastName }) }), dependents?.map((item, index) => (item.relation &&
                                                        (0, jsx_runtime_1.jsx)(react_native_1.View, { style: { flexDirection: 'row' }, children: (0, jsx_runtime_1.jsxs)(react_native_1.Text, { style: [styles.textLarge], children: [item.firstName, " ", item.lastName] }) }, item.dependentId)
                                                        || null))] })] }), (props.carrier == 'AETNA') &&
                                        (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardRow], children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, styles.textBold, { marginRight: 12 }], children: "Group ID" }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, { flex: 1 }], children: memberCards?.aetnaGroupId })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardRow], children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, styles.textBold, { marginRight: 12 }], children: "Member ID" }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, { flex: 1 }], children: memberCards?.aetnaMemberId })] })] }), (props.carrier == 'METLIFE') &&
                                        (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardRow], children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, styles.textBold, { marginRight: 12 }], children: "Group ID" }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, { flex: 1 }], children: memberCards?.metlifeGroupId })] }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [styles.cardRow], children: [(0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, styles.textBold, { marginRight: 12 }], children: "Member ID" }), (0, jsx_runtime_1.jsx)(react_native_1.Text, { style: [styles.textLarge, { flex: 1 }], children: memberCards?.metlifeMemberId })] })] })] })] })] }));
}
